import { BellIcon, CheckIcon, TrashIcon } from '@heroicons/react/24/outline';
import { memo, useCallback, useEffect, useState } from 'react';
import { NOTIFICATIONS } from '../../../../graphql/notification';
import { T, useT } from '../../../../translation/src';
import { useNotificationsDeleteAllMutation, useNotificationsMarkAsReadMutation, useNotificationsQuery } from '../../../../types/graphqlTypes';
import { NotificationListItem } from '../../../user/NotificationList';
import { HeaderDropdownButton, HeaderDropdownButtonProps } from '../HeaderDropdownButton';

const headerIconButtonClassName =
  'flex items-center justify-center p-1 rounded text-gray-500 dark:text-gray-400 cursor-pointer transition-colors disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:text-gray-500 dark:disabled:hover:text-gray-400';

const NotificationsDropdownButton_: React.FC<Pick<HeaderDropdownButtonProps, 'anchor'>> = ({ anchor }) => {
  const notificationsQuery = useNotificationsQuery();
  const notifications = notificationsQuery.data?.notifications || [];
  const [readNotifications, { loading: readNotificationsLoading }] = useNotificationsMarkAsReadMutation();
  const [deleteAllNotifications, { loading: deleteAllLoading }] = useNotificationsDeleteAllMutation();
  const markAsReadTooltip = useT('mark notifications as read', { swc: true });
  const deleteAllTooltip = useT('delete all notifications', { swc: true });
  // Deleting is permanent: the first click asks, a second click within a few seconds deletes.
  const [confirmingDelete, setConfirmingDelete] = useState(false);

  const unreadCount = notifications.filter((n) => !n.isRead).length;

  useEffect(() => {
    if (!confirmingDelete) return;
    const timeout = setTimeout(() => setConfirmingDelete(false), 3000);
    return () => clearTimeout(timeout);
  }, [confirmingDelete]);

  const markAllAsRead = useCallback(() => {
    readNotifications({ refetchQueries: [NOTIFICATIONS], awaitRefetchQueries: true });
  }, [readNotifications]);

  const onDeleteAll = useCallback(() => {
    if (!confirmingDelete) return setConfirmingDelete(true);
    setConfirmingDelete(false);
    deleteAllNotifications({ refetchQueries: [NOTIFICATIONS], awaitRefetchQueries: true });
  }, [confirmingDelete, deleteAllNotifications]);

  return (
    <HeaderDropdownButton
      icon={BellIcon}
      title={<T _str="notifications" swc />}
      badgeCount={unreadCount}
      anchor={anchor}
      panelWidthClassName="w-[22rem] max-w-[calc(100vw-2rem)]"
      headerAction={
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={markAllAsRead}
            disabled={unreadCount === 0 || readNotificationsLoading}
            title={markAsReadTooltip}
            aria-label={markAsReadTooltip}
            className={`${headerIconButtonClassName} hover:text-green-600 dark:hover:text-green-400`}
          >
            <CheckIcon className="w-4 h-4" />
          </button>
          {confirmingDelete ? (
            <button type="button" onClick={onDeleteAll} className="px-2 py-0.5 rounded text-xs font-medium text-white bg-red-500 hover:bg-red-600 cursor-pointer transition-colors">
              <T _str="delete all?" swc />
            </button>
          ) : (
            <button
              type="button"
              onClick={onDeleteAll}
              disabled={notifications.length === 0 || deleteAllLoading}
              title={deleteAllTooltip}
              aria-label={deleteAllTooltip}
              className={`${headerIconButtonClassName} hover:text-red-500 dark:hover:text-red-400`}
            >
              <TrashIcon className="w-4 h-4" />
            </button>
          )}
        </div>
      }
    >
      {({ close }) => (
        <>
          {notifications.length === 0 && (
            <span className="px-3 py-4 text-sm text-gray-500 dark:text-gray-400">
              <T _str="no notifications" swc />
            </span>
          )}
          {notifications.map((notification) => (
            <NotificationListItem key={notification.id} notification={notification} onClose={close} />
          ))}
        </>
      )}
    </HeaderDropdownButton>
  );
};

export const NotificationsDropdownButton = memo(NotificationsDropdownButton_);
